// The server generates conversation titles asynchronously while the first
// answer streams, so the sidebar (which polls /conversations) can briefly see
// the "New conversation" placeholder for a conversation whose real title is
// already known to the chat page. Keep a page-lifetime record of real titles
// and let the sidebar fall back to it, so leaving a conversation never
// regresses its title to the placeholder.
const knownTitles = new Map<string, string>();
export const conversationTitleChangedEvent = "lumen:conversation-title-changed";

export function rememberConversationTitle(id: string, title: string) {
  if (!id || !title || title === "New conversation") return;
  if (knownTitles.get(id) === title) return;
  knownTitles.set(id, title);
  window.dispatchEvent(new Event(conversationTitleChangedEvent));
}

export function rememberConversationTitles(
  conversations: readonly { id: string; title: string }[],
) {
  for (const conversation of conversations)
    rememberConversationTitle(conversation.id, conversation.title);
}

export function knownConversationTitle(id: string): string | undefined {
  return knownTitles.get(id);
}

// Reconcile at the state boundary as well as when rendering the sidebar.
// Navigation clears the open chat's state, so its previous title may only
// exist in the shared record when the next snapshot arrives.
export function preserveConversationTitle<
  T extends { id: string; title: string },
>(incoming: T, current?: { id: string; title: string } | null): T {
  if (incoming.title !== "New conversation") return incoming;
  const title =
    current?.id === incoming.id && current.title !== "New conversation"
      ? current.title
      : knownConversationTitle(incoming.id);
  return title ? { ...incoming, title } : incoming;
}
